import React from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';

const PhoneSettingsModal = ({
  isOpen,
  onClose,
  userPhoneNumbers,
  setUserPhoneNumbers
}) => {
  if (!isOpen) return null;

  const handleSave = () => {
    localStorage.setItem('user_phone_numbers', JSON.stringify(userPhoneNumbers));
    onClose();
    toast.success('Phone numbers saved');
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[500px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-400/20">
          <h2 className="text-2xl font-bold text-white">Phone Numbers</h2>
          <p className="text-gray-400 text-sm mt-1">Set up your phone numbers for sending messages</p>
        </div>

        {/* Phone Numbers Form */}
        <div className="p-6 space-y-4">
          {/* Personal Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              Personal Phone
            </label>
            <input
              type="tel"
              value={userPhoneNumbers.personal}
              onChange={(e) => setUserPhoneNumbers(prev => ({...prev, personal: e.target.value}))}
              placeholder="+1 (555) 123-4567"
              data-testid="personal-phone-input"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <p className="text-xs text-gray-500 mt-1">Your personal mobile number</p>
          </div>

          {/* Business Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              Business Phone
            </label>
            <input
              type="tel"
              value={userPhoneNumbers.business}
              onChange={(e) => setUserPhoneNumbers(prev => ({...prev, business: e.target.value}))}
              placeholder="+1 (555) 987-6543"
              data-testid="business-phone-input"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">Your work or business number</p>
          </div>

          {/* Twilio Phone (Read-only) */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              Twilio Phone
            </label>
            <input
              type="tel"
              value={userPhoneNumbers.twilio || localStorage.getItem('twilio_phone_number') || 'Not configured'}
              readOnly
              data-testid="twilio-phone-input"
              className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Connected via Twilio integration</p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-3 mt-4">
            <p className="text-xs text-gray-400">
              <strong className="text-gray-300">Note:</strong> Messages are sent through your Twilio account. 
              The phone type selection helps you organize which identity you&apos;re using for outgoing messages.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-gray-400/20 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            data-testid="save-phone-numbers-btn"
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors text-white font-medium flex items-center justify-center gap-2"
          >
            <Check size={18} />
            Save Numbers
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneSettingsModal;
